import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GUIDES } from "../data/guidesData";

const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g400: "#28b566",
  g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268",
  n400: "#8a93a8", n300: "#b0b8cc", n200: "#d1d5e0", n150: "#e4e7ef",
  n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ── Progress bar ── */
function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, height: 3,
      zIndex: 9999, background: T.n100,
    }}>
      <div style={{
        height: "100%", width: `${pct}%`,
        background: `linear-gradient(90deg, ${T.g400}, ${T.g600})`,
        transition: "width .1s linear",
      }} />
    </div>
  );
}

/* ── Section with anchor ── */
function Section({ section, index }) {
  const id = `section-${index}`;
  return (
    <div id={id} style={{ scrollMarginTop: 32, marginBottom: 52 }}>
      <h2 style={{
        fontFamily: T.serif,
        fontSize: "clamp(20px,3.5vw,26px)",
        fontWeight: 400,
        color: T.n900,
        margin: "0 0 20px",
        letterSpacing: "-0.025em",
        lineHeight: 1.25,
      }}>
        {section.heading}
      </h2>
      {section.body.trim().split(/\n\n+/).map((para, i) => (
        <p key={i} style={{
          fontSize: "clamp(15px,2vw,17px)",
          color: T.n600,
          lineHeight: 1.85,
          margin: "0 0 20px",
          fontFamily: T.sans,
        }}>
          {para.trim()}
        </p>
      ))}
    </div>
  );
}

/* ── TOC item ── */
function TocItem({ section, index, active }) {
  const scrollTo = () => {
    document.getElementById(`section-${index}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button onClick={scrollTo} style={{
      display: "block", width: "100%", textAlign: "left",
      background: "none", border: "none", cursor: "pointer",
      padding: "8px 0 8px 14px",
      borderLeft: `2px solid ${active ? T.g500 : T.n150}`,
      marginLeft: -2,
      fontFamily: T.sans,
      fontSize: 12,
      color: active ? T.n900 : T.n400,
      fontWeight: active ? 600 : 400,
      lineHeight: 1.5,
      transition: "color .15s, border-color .15s",
    }}>
      {section.heading}
    </button>
  );
}

/* ── Related guide card ── */
function RelatedCard({ guide, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 14, border: `1.5px solid ${hovered ? T.g200 : T.n150}`,
        background: T.white, overflow: "hidden", cursor: "pointer",
        transition: "border-color .18s, box-shadow .18s",
        boxShadow: hovered ? "0 4px 20px rgba(30,146,84,.08)" : "none",
        display: "flex", flexDirection: "column",
      }}>
      <div style={{ height: 120, background: guide.color, overflow: "hidden" }}>
        <img src={guide.image} alt={guide.title}
          onError={e => { e.target.style.display = "none"; }}
          style={{
            width: "100%", height: "100%", objectFit: "cover", display: "block",
            transform: hovered ? "scale(1.04)" : "scale(1)",
            transition: "transform .4s cubic-bezier(.25,.46,.45,.94)",
          }} />
      </div>
      <div style={{ padding: "16px", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, color: T.g600,
            background: T.g50, border: `1px solid ${T.g100}`,
            borderRadius: 99, padding: "2px 8px", letterSpacing: "0.05em", textTransform: "uppercase",
          }}>{guide.category}</span>
          <span style={{ fontSize: 11, color: T.n400 }}>{guide.readTime}</span>
        </div>
        <h4 style={{
          fontFamily: T.serif, fontSize: "clamp(14px,2vw,17px)", fontWeight: 400,
          color: T.n900, margin: "0 0 6px", letterSpacing: "-0.015em", lineHeight: 1.3,
        }}>{guide.title}</h4>
        <p style={{ fontSize: 12, color: T.n500, lineHeight: 1.6, margin: 0 }}>{guide.description}</p>
      </div>
    </div>
  );
}

/* ══ MAIN PAGE ══ */
export default function GuideArticlePage() {
  useFonts();
  const navigate = useNavigate();
  const { id } = useParams();

  // Support both numeric id and slug
  const guide = GUIDES.find(g => String(g.id) === String(id) || g.slug === id);

  const [activeSection, setActiveSection] = useState(0);
  const [tocVisible, setTocVisible]       = useState(false);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  // Active section tracking
  useEffect(() => {
    if (!guide) return;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.id.replace("section-", ""), 10);
            if (!isNaN(idx)) setActiveSection(idx);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    guide.sections.forEach((_, i) => {
      const el = document.getElementById(`section-${i}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [guide]);

  if (!guide) {
    return (
      <div style={{ fontFamily: T.sans, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, color: T.n600, padding: 40 }}>
        <div style={{ fontFamily: T.serif, fontSize: 48, color: T.n200 }}>404</div>
        <p style={{ fontSize: 16, margin: 0 }}>Guide not found.</p>
        <button onClick={() => navigate("/guides")} style={{
          padding: "10px 24px", background: T.g500, color: T.white,
          border: "none", borderRadius: 9, fontSize: 14, fontWeight: 600,
          cursor: "pointer", fontFamily: T.sans,
        }}>
          Back to guides
        </button>
      </div>
    );
  }

  const related = GUIDES.filter(g => g.id !== guide.id).slice(0, 3);

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        @media (min-width: 900px) {
          .article-layout { display: grid !important; grid-template-columns: 1fr 220px; gap: 60px; }
          .mobile-toc { display: none !important; }
          .desktop-toc { display: block !important; }
        }
        @media (max-width: 899px) {
          .desktop-toc { display: none !important; }
        }
      `}</style>

      <ReadingProgress />

      {/* ── Nav ── */}
      <div style={{
        padding: "16px 24px", borderBottom: `1px solid ${T.n100}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 3, background: T.white, zIndex: 100,
      }}>
        <button onClick={() => navigate("/guides")} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          color: T.n400, fontSize: 13, fontFamily: T.sans, padding: 0,
        }}
          onMouseEnter={e => e.currentTarget.style.color = T.n800}
          onMouseLeave={e => e.currentTarget.style.color = T.n400}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
          All guides
        </button>

        {/* Mobile TOC toggle */}
        <button className="mobile-toc" onClick={() => setTocVisible(v => !v)} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: T.n50, border: `1px solid ${T.n150}`, cursor: "pointer",
          color: T.n600, fontSize: 12, fontFamily: T.sans,
          padding: "7px 13px", borderRadius: 8, fontWeight: 500,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          Contents
        </button>
      </div>

      {/* Mobile TOC drawer */}
      {tocVisible && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 500,
          background: "rgba(15,17,23,.45)", backdropFilter: "blur(4px)",
        }} onClick={() => setTocVisible(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            background: T.white, borderRadius: "20px 20px 0 0",
            padding: "28px 24px 48px", maxHeight: "70vh", overflowY: "auto",
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 16 }}>
              In this guide
            </div>
            {guide.sections.map((s, i) => (
              <button key={i} onClick={() => {
                setTocVisible(false);
                setTimeout(() => document.getElementById(`section-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
              }} style={{
                display: "block", width: "100%", textAlign: "left",
                background: "none", border: "none", cursor: "pointer",
                padding: "11px 0", borderBottom: `1px solid ${T.n100}`,
                fontFamily: T.sans, fontSize: 14, color: T.n700,
                fontWeight: activeSection === i ? 600 : 400,
              }}>
                <span style={{ fontSize: 11, color: T.g500, fontWeight: 700, marginRight: 8 }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.heading}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ══ HERO ══ */}
      <div style={{ borderBottom: `1px solid ${T.n100}` }}>
        {/* Cover image */}
        <div style={{ height: "clamp(200px,32vw,380px)", background: guide.color, overflow: "hidden" }}>
          <img src={guide.image} alt={guide.title}
            onError={e => { e.target.style.display = "none"; }}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>

        {/* Meta */}
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "clamp(32px,5vw,52px) 24px clamp(28px,4vw,44px)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
            <span style={{
              fontSize: 11, fontWeight: 700, color: T.g600,
              background: T.g50, border: `1px solid ${T.g100}`,
              borderRadius: 99, padding: "3px 10px", letterSpacing: "0.05em", textTransform: "uppercase",
            }}>{guide.category}</span>
            <span style={{ fontSize: 13, color: T.n400 }}>{guide.readTime}</span>
            <span style={{ fontSize: 13, color: T.n300 }}>·</span>
            <span style={{ fontSize: 13, color: T.n400 }}>{guide.sections.length} sections</span>
          </div>

          <h1 style={{
            fontFamily: T.serif,
            fontSize: "clamp(30px,5.5vw,52px)",
            fontWeight: 400,
            margin: "0 0 18px",
            lineHeight: 1.08,
            color: T.n900,
            letterSpacing: "-0.03em",
            maxWidth: 680,
          }}>
            {guide.title}
          </h1>

          <p style={{
            fontSize: "clamp(15px,2vw,17px)",
            color: T.n600,
            lineHeight: 1.75,
            margin: 0,
            maxWidth: 580,
          }}>
            {guide.description}
          </p>
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 24px clamp(80px,10vw,120px)" }}>
        <div className="article-layout" style={{ display: "block", paddingTop: "clamp(40px,6vw,64px)" }}>

          {/* ── Article content ── */}
          <div>
            {/* Intro */}
            <div style={{
              marginBottom: 52,
              padding: "28px 28px",
              borderRadius: 16,
              background: guide.color || T.g50,
              border: `1px solid ${T.g100}`,
            }}>
              {guide.intro.split(/\n\n+/).map((para, i) => (
                <p key={i} style={{
                  fontSize: "clamp(15px,2vw,17px)",
                  color: T.n700,
                  lineHeight: 1.85,
                  margin: i < guide.intro.split(/\n\n+/).length - 1 ? "0 0 18px" : 0,
                  fontFamily: T.sans,
                }}>
                  {para.trim()}
                </p>
              ))}
            </div>

            {/* Sections */}
            {guide.sections.map((section, i) => (
              <Section key={i} section={section} index={i} />
            ))}

            {/* End CTA */}
            <div style={{
              marginTop: 20, padding: "clamp(28px,4vw,40px)",
              borderRadius: 18, border: `1px solid ${T.n150}`,
              background: T.n50, textAlign: "center",
            }}>
              <h3 style={{
                fontFamily: T.serif, fontSize: "clamp(22px,4vw,32px)", fontWeight: 400,
                color: T.n900, margin: "0 0 10px", letterSpacing: "-0.025em", lineHeight: 1.2,
              }}>
                Ready to put this into{" "}
                <em style={{ fontStyle: "italic", color: T.g600 }}>practice?</em>
              </h3>
              <p style={{ fontSize: 15, color: T.n400, margin: "0 0 24px", lineHeight: 1.7, maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>
                Create your JiraniPro profile today and start applying what you have just read from day one.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={() => navigate("/pro-onboarding")} style={{
                  padding: "12px 26px", background: T.g500, color: T.white,
                  border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600,
                  cursor: "pointer", fontFamily: T.sans,
                  display: "inline-flex", alignItems: "center", gap: 7,
                }}
                  onMouseEnter={e => e.currentTarget.style.background = T.g600}
                  onMouseLeave={e => e.currentTarget.style.background = T.g500}>
                  Become a JiraniPro
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>
                <button onClick={() => navigate("/guides")} style={{
                  padding: "12px 22px", background: "transparent", color: T.n700,
                  border: `1.5px solid ${T.n200}`, borderRadius: 10, fontSize: 14, fontWeight: 600,
                  cursor: "pointer", fontFamily: T.sans,
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = T.n400}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.n200}>
                  Browse all guides
                </button>
              </div>
            </div>

            {/* Related guides */}
            {related.length > 0 && (
              <div style={{ marginTop: 56 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.g500, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 20px" }}>
                  More guides
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
                  {related.map(g => (
                    <RelatedCard key={g.id} guide={g} onClick={() => navigate(`/guides/${g.id}`)} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Desktop sticky TOC ── */}
          <aside className="desktop-toc" style={{ display: "none" }}>
            <div style={{ position: "sticky", top: 80 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.n400, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14 }}>
                In this guide
              </div>
              <nav style={{ borderLeft: `2px solid ${T.n150}`, paddingLeft: 0, marginLeft: 2 }}>
                {guide.sections.map((s, i) => (
                  <TocItem key={i} section={s} index={i} active={activeSection === i} />
                ))}
              </nav>

              {/* Progress indicator */}
              <div style={{ marginTop: 28, padding: "14px 0", borderTop: `1px solid ${T.n100}` }}>
                <div style={{ fontSize: 11, color: T.n400, marginBottom: 8 }}>
                  Section {activeSection + 1} of {guide.sections.length}
                </div>
                <div style={{ height: 3, borderRadius: 99, background: T.n150, overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${((activeSection + 1) / guide.sections.length) * 100}%`,
                    background: `linear-gradient(90deg, ${T.g400}, ${T.g600})`,
                    transition: "width .3s ease",
                    borderRadius: 99,
                  }} />
                </div>
              </div>

              {/* Back to guides */}
              <button onClick={() => navigate("/guides")} style={{
                marginTop: 20, display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "none", cursor: "pointer",
                color: T.n400, fontSize: 12, fontFamily: T.sans, padding: 0,
              }}
                onMouseEnter={e => e.currentTarget.style.color = T.n700}
                onMouseLeave={e => e.currentTarget.style.color = T.n400}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                All guides
              </button>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}